'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, token } from '@terra/ui-styled-system';
import { Button, MetaDefault, MonoSmall } from '@terra/ui-design-system';
import { useTargetId, useTargetType } from '../../../store/projectStore';
import { useFindingsCounts } from '../../../hooks/api/useFindings';
import type { FindingsListFilters } from '../findings-table/useFindingsListFilters';

type SeverityChangeRequestsFilterChipProps = {
  filters: FindingsListFilters;
};

export function SeverityChangeRequestsFilterChip({
  filters,
}: SeverityChangeRequestsFilterChipProps) {
  const t = useTranslations('proxy.findings.filters');
  const targetId = useTargetId();
  const targetType = useTargetType();
  const pendingCountFilter = useMemo(
    () => ({
      ...filters.filter,
      hasPendingSeverityChangeRequest: true,
    }),
    [filters.filter],
  );
  const { matchingCount } = useFindingsCounts({
    targetId,
    targetType,
    filter: pendingCountFilter,
  });
  const isActive = filters.hasPendingSeverityChangeRequestFilter;
  const count = matchingCount ?? 0;

  return (
    <Button
      type="button"
      size="2xs"
      variant="outline"
      display="inline-flex"
      alignItems="center"
      alignSelf="center"
      gap="1.5"
      h={6}
      maxH={6}
      minH={6}
      minW="unset"
      px="2"
      borderRadius="2xl"
      borderColor={isActive ? 'border.accent.default' : 'border.neutral.subtle'}
      bg="bg.neutral.default"
      boxShadow="none"
      cursor="pointer"
      flexShrink={0}
      lineHeight="1"
      overflow="hidden"
      aria-pressed={isActive}
      onClick={() =>
        filters.setHasPendingSeverityChangeRequestFilter(!isActive)
      }
      _hover={{
        bg: 'bg.neutral.subtleSoft',
      }}
    >
      <Box
        w="1.5"
        h="1.5"
        borderRadius="full"
        bg={token('colors.fg.severity.high')}
        flexShrink={0}
      />
      <MetaDefault
        fontWeight="normal"
        whiteSpace="nowrap"
        color={isActive ? 'fg.neutral.muted' : 'fg.neutral.subtle'}
        lineHeight="1"
      >
        {t('severityChangeRequests')}
      </MetaDefault>
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        alignSelf="center"
        h={4}
        minH={4}
        maxH={4}
        px="1"
        borderRadius="sm"
        bg="bg.severity.high"
        flexShrink={0}
        lineHeight="1"
      >
        <MonoSmall color="fg.severity.high" lineHeight="1">
          {count.toLocaleString()}
        </MonoSmall>
      </Box>
    </Button>
  );
}
